'use client';
import React from 'react';
import styles from './episode.module.sass';
import cn from 'classnames';
import { FontIcon } from '../FontIcon';
import { Skeleton } from '../Skeleton';
import { LazyImage } from '../LazyImage';
import { LinkProps, LinkWrapper } from '../LinkWrapper';
import { useMediaQuery } from '@intigral/hooks/dom/useMediaQuery/useMediaQuery';

interface EpisodeProps extends React.HTMLAttributes<HTMLDivElement> {
    image: string;
    number: string;
    title: string;
    duration?: string;
    progress?: string;
    description?: string;
    link: LinkProps;
    isLoading?: boolean;
    isLocked?: boolean;
    isLockedFunc?: any;
    canPlayContent?: boolean;
    showParentalPopupMessage?: any;
    /**
     * If true, the episode description will be displayed under the image on desktop view.
     */
    isVertical?: boolean;
}

export const Episode = ({
    image,
    number,
    title,
    duration,
    description,
    progress,
    link,
    isLoading,
    className,
    isLocked,
    isLockedFunc = undefined,
    canPlayContent = true,
    showParentalPopupMessage,
    isVertical,
    ...props
}: EpisodeProps) => {
    const classes = cn(styles.episode, className, {
        [styles.vertical]: isVertical,
    });

    const isMobile = useMediaQuery('(max-width: 924px)');

    if (isLoading) {
        return (
            <div className={classes} {...props}>
                <Skeleton variant="rounded" absolute />
            </div>
        );
    }

    return (
        <div className={classes} {...props}>
            <LinkWrapper
                link={link}
                className={styles.holder}
                onClick={(e) => {
                    if (isLocked) {
                        e.preventDefault(); // Prevent the default action (redirection)
                        isLockedFunc(); // Run the isLockedFunc
                    }

                    if (!canPlayContent) {
                        e.preventDefault();
                        showParentalPopupMessage();
                    }
                }}
            >
                <div className={styles.image}>
                    <img src={image} alt={title} />
                    {!isLocked && <FontIcon name="play" size="large" md="x-large" />}
                    {isLocked && (
                        <div className={cn(styles.icon, `${styles['lock']}`)}>
                            <FontIcon name={'lock'} size="x-small" md="smaller" />
                        </div>
                    )}
                    {progress && (
                        <span className={styles.progress}>
                            <i style={{ width: progress }} />
                        </span>
                    )}
                </div>
            </LinkWrapper>

            <div className={styles.info}>
                <span>{number}</span>
                <strong>
                    <LinkWrapper link={link}>{title}</LinkWrapper>
                </strong>
                {isMobile && <span className={styles.duration}>{duration}</span>}
            </div>
            <div className={styles.description}>{description}</div>
            {!isMobile && <span className={styles.duration}>{duration}</span>}
        </div>
    );
};
